type Gtag = (...args: unknown[]) => void;

interface AnalyticsWindow extends Window {
  dataLayer?: unknown[];
  gtag?: Gtag;
}

function removeGoogleAnalyticsCookies() {
  const cookieNames = document.cookie
    .split(";")
    .map(cookie => cookie.trim().split("=")[0])
    .filter(name => name === "_ga" || name.startsWith("_ga_"));

  const rootDomain = window.location.hostname.replace(/^www\./, "");

  for (const name of cookieNames) {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.${rootDomain}; SameSite=Lax`;
  }
}

export default defineNuxtPlugin(() => {
  if (import.meta.dev) return;

  const config = useRuntimeConfig();
  const googleAnalyticsId = String(
    config.public.googleAnalyticsId || "",
  ).trim();

  if (!googleAnalyticsId) return;

  const router = useRouter();
  const { analyticsAccepted } = useCookieConsent();
  const analyticsWindow = window as AnalyticsWindow;

  let initialized = false;
  let lastTrackedLocation = "";

  function ensureGoogleAnalytics() {
    if (initialized) return;

    initialized = true;
    analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
    analyticsWindow.gtag = function (..._args: unknown[]) {
      // Google gtag intentionally queues the function arguments object
      // eslint-disable-next-line prefer-rest-params
      analyticsWindow.dataLayer?.push(arguments);
    };

    analyticsWindow.gtag("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });

    analyticsWindow.gtag("js", new Date());
    analyticsWindow.gtag("config", googleAnalyticsId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleAnalyticsId)}`;
    document.head.appendChild(script);
  }

  function trackPageView() {
    if (!analyticsAccepted.value || !analyticsWindow.gtag) return;

    const pageLocation = window.location.href;
    if (pageLocation === lastTrackedLocation) return;

    lastTrackedLocation = pageLocation;
    analyticsWindow.gtag("event", "page_view", {
      page_title: document.title,
      page_location: pageLocation,
      page_path: router.currentRoute.value.fullPath,
    });
  }

  function enableAnalytics() {
    Reflect.set(analyticsWindow, `ga-disable-${googleAnalyticsId}`, false);
    ensureGoogleAnalytics();

    analyticsWindow.gtag?.("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });

    nextTick(trackPageView);
  }

  function disableAnalytics() {
    Reflect.set(analyticsWindow, `ga-disable-${googleAnalyticsId}`, true);

    analyticsWindow.gtag?.("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });

    removeGoogleAnalyticsCookies();
  }

  watch(
    analyticsAccepted,
    accepted => {
      if (accepted) {
        enableAnalytics();
      } else {
        disableAnalytics();
      }
    },
    { immediate: true },
  );

  router.afterEach(() => {
    if (analyticsAccepted.value) {
      nextTick(trackPageView);
    }
  });
});
